import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, finalize } from 'rxjs';
import { map } from 'rxjs/operators';

import { PersonaProviderProfile } from '../models/persona-provider-profile';
import { PersonaProvidersHttpService } from '../services/persona-providers.httpservice';

@Injectable({
  providedIn: 'root'
})

export class PersonaProviderStore {
  private providerProfileSubj: BehaviorSubject<PersonaProviderProfile> = new BehaviorSubject<PersonaProviderProfile>({} as PersonaProviderProfile);
  providerProfile$ = this.providerProfileSubj.asObservable();

  private savingAvailabilitySubj: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  savingAvailability$: Observable<boolean> = this.savingAvailabilitySubj.asObservable();

  private savingAllowsConsultationSubj: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  savingAllowsConsultation$: Observable<boolean> = this.savingAllowsConsultationSubj.asObservable();

  constructor(private personaProviderService: PersonaProvidersHttpService) {
    this.loadProfile();
  }

  loadProfile(): void {
    this.personaProviderService.getPersonaProviderProfile()
      .subscribe({
        next: (response) => {
          this.providerProfileSubj.next(response);
        },
        error: (err) => {
          // eslint-disable-next-line no-console
          console.error(err);
        }
      });
  }

  updateAvailability(availability: boolean, personaProviderId: number): void {
    this.personaProviderService.updateGeneralAvailability(availability, personaProviderId)
      .pipe(
        finalize(() => {
          this.savingAvailabilitySubj.next(false);
        })
      )
      .subscribe({
        next: (response: PersonaProviderProfile) => {
          this.providerProfileSubj.next(response);
        },
        error: (err) => {
          // eslint-disable-next-line no-console
          console.error(err);
        }
      });
  }

  updateAllowsConsultation(allowsConsultation: boolean, personaProviderId: number): void {
    this.toggleSavingAllowsConsultation(true);
    this.personaProviderService.updateAllowsConsultation(allowsConsultation, personaProviderId)
      .pipe(
        finalize(() => {
          this.toggleSavingAllowsConsultation(false);
        })
      )
      .subscribe({
        next: (response: PersonaProviderProfile) => {
          this.providerProfileSubj.next(response);
        },
        error: (err) => {
          // eslint-disable-next-line no-console
          console.error(err);
        }
      });
  }

  updateSessionTypesOffered(sessionTypesOffered: string[], personaProviderId: number): void {
    this.personaProviderService.updateSessionTypesOffered(sessionTypesOffered, personaProviderId)
      .subscribe({
        next: (response: PersonaProviderProfile) => {
          this.providerProfileSubj.next(response);
        },
        error: (error) => {
          // eslint-disable-next-line no-console
          console.error(error);
        }
      });
  }

  updateAcceptingSamClients(availability: boolean, personaProviderId: number): void {
    this.personaProviderService.updateAcceptingSamClients(availability, personaProviderId)
      .subscribe({
        next: (response: PersonaProviderProfile) => {
          this.providerProfileSubj.next(response);
        },
        error: (err) => {
          // eslint-disable-next-line no-console
          console.error(err);
        }
      });
  }

  updateDirectSchedulePref(enabledStatus: boolean, personaProviderId: number): void {
    this.personaProviderService.updateDirectSchedulePref(enabledStatus, personaProviderId)
      .subscribe({
        next: () => {
          this.loadProfile();
        },
        error: (err) => {
          // eslint-disable-next-line no-console
          console.error(err);
        }
      });
  }

  updateLgbtIdentified$(lgbtIdentified: string, personaProviderId: number): Observable<PersonaProviderProfile> {
    return this.personaProviderService.updateLgbtIdentified$(lgbtIdentified, personaProviderId)
      .pipe(
        map((provider: PersonaProviderProfile) => {
          this.providerProfileSubj.next(provider);
          return provider;
        })
      );
  }

  toggleSavingAvailability(isSaving: boolean): void {
    this.savingAvailabilitySubj.next(isSaving);
  }

  toggleSavingAllowsConsultation(isSaving: boolean): void {
    this.savingAllowsConsultationSubj.next(isSaving);
  }
}
